import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, MessageCircleHeart, Briefcase, PhoneCall, Shield, Activity, AlertCircle, CheckSquare, CalendarClock, GraduationCap } from 'lucide-react';
interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (sectionId: string) => void;
}
const SECTIONS = [{
  id: 'sentiment',
  name: 'Sentiment Analysis',
  icon: MessageCircleHeart,
  score: 8
}, {
  id: 'deal',
  name: 'Deal Possibilities',
  icon: Briefcase,
  score: 9
}, {
  id: 'callReview',
  name: 'Call Review',
  icon: PhoneCall,
  score: 8
}, {
  id: 'objections',
  name: 'Objection Handling',
  icon: Shield,
  score: 6
}, {
  id: 'behavior',
  name: 'Behavior Analysis',
  icon: Activity
}, {
  id: 'missed',
  name: 'Call Coaching',
  icon: GraduationCap
}, {
  id: 'action',
  name: 'Action Items',
  icon: CheckSquare
}, {
  id: 'followUp',
  name: 'Follow Up',
  icon: CalendarClock
}];
export function CommandPalette({
  isOpen,
  onClose,
  onNavigate
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const filteredSections = SECTIONS.filter(section => section.name.toLowerCase().includes(query.toLowerCase()));
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredSections.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredSections.length) % filteredSections.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredSections[selectedIndex]) {
          onNavigate(filteredSections[selectedIndex].id);
          onClose();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredSections, selectedIndex, onNavigate, onClose]);
  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => part.toLowerCase() === query.toLowerCase() ? <span key={i} className="text-crimson font-medium" data-id="element-93">
          {part}
        </span> : part);
  };
  return <AnimatePresence data-id="element-94">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-95" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: -20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: -20
      }} transition={{
        duration: 0.15,
        ease: 'easeOut'
      }} className="fixed top-[15%] left-1/2 -translate-x-1/2 w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden z-50 border border-warmgray" data-id="element-96">
            <div className="flex items-center px-4 py-3 border-b border-warmgray" data-id="element-97">
              <Search className="text-gray-400 mr-3" size={20} data-id="element-98" />
              <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search sections..." className="flex-1 bg-transparent border-none outline-none text-gray-800 placeholder-gray-400 text-lg" data-id="element-99" />
              <div className="flex items-center gap-1 text-xs text-gray-400 font-medium bg-gray-100 px-2 py-1 rounded" data-id="element-100">
                <span data-id="element-101">esc</span>
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2" data-id="element-102">
              {filteredSections.length === 0 ? <div className="py-8 text-center text-gray-500" data-id="element-103">
                  No sections found for "{query}"
                </div> : filteredSections.map((section, index) => {
            const Icon = section.icon;
            const isSelected = index === selectedIndex;
            return <button key={section.id} onClick={() => {
              onNavigate(section.id);
              onClose();
            }} onMouseEnter={() => setSelectedIndex(index)} className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${isSelected ? 'bg-crimson/10 text-navy' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-104">
                      <div className="flex items-center gap-3" data-id="element-105">
                        <Icon size={18} className={isSelected ? 'text-crimson' : 'text-gray-400'} data-id="element-106" />
                        <span className="text-sm font-medium" data-id="element-107">
                          {highlightMatch(section.name, query)}
                        </span>
                      </div>
                      {section.score !== undefined && <div className={`text-xs font-serif font-bold px-2 py-0.5 rounded ${isSelected ? 'bg-crimson text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-108">
                          Score: {section.score}
                        </div>}
                    </button>;
          })}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}